import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { ILT_QUERY, ILT_REGISTER_MUTATION, ILT_REGISTRATION_QUERY, ILT_UNREGISTER_MUTATION } from '../gql/queries/ILT_QUERY';
import { PATCH_ASSIGNMENT_MUTATION } from '../gql';
import { useAuthState } from './auth';
import { getFormattedSession } from '../components/SessionView/Sessions';
import { useTranslations } from './translations';

const useIlt = ( updateLessonCb ) => {

    const { locale } = useTranslations();
    const { claims } = useAuthState();

    const [ iltId, setIltId ] = useState();
    const [ ilt, setIlt ] = useState();
    const [ iltRegistrationInfo, setIltRegistrationInfo ] = useState();
    const [ course, setCourse ] = useState();

    const [ loading, setLoading ] = useState( false );
    const [ forceLoading, setForceLoading ] = useState( false );

    const [ patchAssignmentMutation ] = useMutation( PATCH_ASSIGNMENT_MUTATION );
    const [ fetchIltRegistration ] = useLazyQuery( ILT_REGISTRATION_QUERY, { fetchPolicy: 'network-only', onCompleted: onIltRegistrationQueryCompleted } ); 
    const [ fetchIltQuery ] = useLazyQuery( ILT_QUERY, { fetchPolicy: 'network-only', onCompleted: onFetchIltCompleted } ); // { iltID }
    const [ registerSessionMutation ] = useMutation( ILT_REGISTER_MUTATION, { onCompleted: updateRegistration } ); // { input: { iltID, sessionID } }
    const [ unregisterSessionMutation ] = useMutation( ILT_UNREGISTER_MUTATION, { onCompleted: updateRegistration } ); // { iltID, sessionID, registrationID }

    function getIltIdFromCourse( course ) {
        return course?.learningObjects[ 0 ].assets.find( a => !!a.asset.iltId ).asset.iltId;
    }

    function onFetchIltCompleted( data ) {
        const iltData = data.instructorLedTraining;
        const sessions = iltData.sessions.map( s => getFormattedSession( s, course, locale ) );
        iltData.sessions = sessions;
        setIlt( iltData );
        setLoading( false );
    }

    function loadIlt( course ) {
        setLoading( true );
        setCourse( course );
        const id = getIltIdFromCourse( course );
        setIltId( id );
        fetchIltQuery( {
            variables: {
                _state: Math.random(), // needed to trigger onCompleted with Apollo bug
                iltID: id
            }
        } );
        fetchRegistrationInfo( id );
    }

    function fetchRegistrationInfo( iltId ) {
        fetchIltRegistration( {
            variables: {
                _state: Math.random(), // needed to trigger onCompleted with Apollo bug
                iltID: iltId, 
                personID: claims.person_id
            }
        } );
    }

    async function registerForSession( iltId, sessionId ) {

        await registerSessionMutation( {
            variables: {
                _state: Math.random(), // needed to trigger onCompleted with Apollo bug
                input: {
                    iltID: iltId,
                    sessionID: sessionId,
                }
            }
        } );
    }

    async function unregisterForSession( iltId, sessionId, registrationId ) {

        await unregisterSessionMutation( {
            variables: {
                _state: Math.random(), // needed to trigger onCompleted with Apollo bug
                iltID: iltId, 
                sessionID: sessionId,
                registrationID: registrationId
            }
        } );
    }

    function isIltCourse( course ) {
        return course?.isIlt;
    }
    
    async function updateRegistration( { createSessionRegistration, deleteSessionRegistration, createSessionCompletion }, fakeUpdate = false ) {

        setLoading( false );
        setForceLoading( true );
        if ( fakeUpdate ) {
            return;
        }

        function updateIltReg( props ) {
            setIltRegistrationInfo( {
                ...iltRegistrationInfo,
                ...props,
            } );
        }

        async function patchAssignment( status ) {
            const lessonId = course.learningObjects[ 0 ].id;
            await patchAssignmentMutation( { variables: { input: {
                learningObjectID: lessonId,
                courseID: course.id,
                status
            } } } );
            updateLessonCb( {
                id: lessonId,
                status
            }, false );
        }
        
        if ( createSessionRegistration ) {
            updateIltReg( {
                isRegistered: true,
                registrationId: createSessionRegistration.id,
                sessionId: createSessionRegistration.sessionId,
            } );
            await patchAssignment( 'IN_PROGRESS' );
        } else if ( deleteSessionRegistration ) {
            updateIltReg( {
                isRegistered: false,
                registrationId: undefined,
                sessionId: undefined,
            } );
            await patchAssignment( 'IN_PROGRESS' );
        } else if ( createSessionCompletion ) {
            updateIltReg( {
                isCompleted: true,
                completionCode: createSessionCompletion?.completionCode,
            } );
            await patchAssignment( 'COMPLETED' );
        }

        if ( !fakeUpdate ) {
            fetchRegistrationInfo( iltId );
        } else {
            setForceLoading( false );
        }
    }

    async function onIltRegistrationQueryCompleted( registrationRes ) {
        const res = registrationRes.getSessionRegistrationByPerson;
        const iltRegistration = {
            isCompleted: !!res?.sessionCompletion?.id,
            isRegistered: !!res?.sessionRegistration?.id,
            iltId,
            completionCode: res?.sessionCompletion?.completionCode,
            registrationId: res?.sessionRegistration?.id,
            sessionId: res?.sessionRegistration?.sessionId,
            register: async ( sessionId ) => await registerForSession( iltId, sessionId ),
            unregister: async ( sessionId, registrationId ) => await unregisterForSession( iltId, sessionId, registrationId ),
        };

        setIltRegistrationInfo( iltRegistration );
        setForceLoading( false );
        setLoading( false );
    }

    return {
        loadIlt,
        ilt, forceLoading, loading, iltRegistrationInfo,
        isIltCourse,
        updateRegistration,
    };

};


export default useIlt;